// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/stay/StaySheet.tsx
// 🔧 PURPOSE: Il foglio dal basso di "Il mio soggiorno" nel nuovo stile
//             (come LaundryPaySheet): fondo avorio, la maniglia, il quadrato
//             colorato con l'icona, il titolo in Fraunces e la X da 44px.
//             Lo usano la tassa di soggiorno, la richiesta della ricevuta,
//             il ritiro rifiuti, la promozione a 5 stelle e l'avviso della
//             prenotazione annullata (con una testata sua, se serve).
//
//             Portale su <body>, sopra la barra in basso. Esc chiude, Tab
//             resta nel foglio, il focus parte dalla X e alla chiusura torna
//             a chi l'ha aperto. La pagina sotto non scorre finché è aperto;
//             la barra di stato si vela come la pagina (lib/statusBar).
// ============================================

import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '../../lib/utils';
import { useStatusBarVeil } from '../../lib/statusBar';
import { CreamClose, INK_TEXT, MUTED_TEXT } from './ui';

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface StaySheetProps {
  open: boolean;
  onClose: () => void;
  /** L'id del titolo (aria-labelledby). */
  titleId: string;
  /** Il testo del titolo nella testata di base. */
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  /** L'icona nel quadrato da 44px e i suoi colori (fondo + icona, con dark:). */
  icon?: React.ReactNode;
  iconClassName?: string;
  /** Prima della X (l'importo, in Fraunces). */
  trailing?: React.ReactNode;
  /** L'etichetta della X. */
  closeLabel: string;
  /** Una testata tutta sua al posto di quella di base (la X va marcata data-sheet-close). */
  header?: React.ReactNode;
  children: React.ReactNode;
  /** La parte ferma in fondo (il bottone d'invio), fuori dallo scorrimento. */
  footer?: React.ReactNode;
  /** Un tocco sul velo chiude? (di base sì) */
  closeOnOverlay?: boolean;
  /** Esc: di base come la X. */
  onEscape?: () => void;
  role?: 'dialog' | 'alertdialog';
  describedBy?: string;
  /** Classi in più per il pannello (es. lo sfondo della conferma). */
  panelClassName?: string;
}

const StaySheet: React.FC<StaySheetProps> = ({
  open,
  onClose,
  titleId,
  title,
  subtitle,
  icon,
  iconClassName,
  trailing,
  closeLabel,
  header,
  children,
  footer,
  closeOnOverlay = true,
  onEscape,
  role = 'dialog',
  describedBy,
  panelClassName,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const onEscapeRef = useRef(onEscape || onClose);
  onEscapeRef.current = onEscape || onClose;

  // Il velo espresso scurisce anche la barra di stato.
  useStatusBarVeil(0.55, open, { tint: '#2a2620', darkAlpha: 0.6, darkTint: '#000000' });

  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Il fuoco sulla X (o sul primo comando), appena il pannello c'è.
    const focusId = window.setTimeout(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const close = panel.querySelector<HTMLElement>('[data-sheet-close]');
      const first = panel.querySelector<HTMLElement>(FOCUSABLE);
      (close || first)?.focus({ preventScroll: true });
    }, 0);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onEscapeRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const focusables = Array.prototype.slice.call(panelRef.current.querySelectorAll(FOCUSABLE)) as HTMLElement[];
      if (focusables.length === 0) return;
      const firstEl = focusables[0];
      const lastEl = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(focusId);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) {
        opener.focus({ preventScroll: true });
      }
    };
  }, [open]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[9999]" role={role} aria-modal="true" aria-labelledby={titleId} aria-describedby={describedBy}>
          <motion.div
            className="absolute inset-0 bg-nva-espresso/55 dark:bg-black/60"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeOnOverlay ? onClose : undefined}
          />
          <motion.div
            ref={panelRef}
            className={cn(
              'absolute inset-x-0 bottom-0 mx-auto flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-t-[22px] bg-nva-bone shadow-[0_-12px_40px_rgba(42,38,32,.3)] dark:bg-stone-900',
              panelClassName,
            )}
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
          >
            {header ?? (
              <>
                <div className="flex justify-center pt-2">
                  <span aria-hidden="true" className="h-1 w-10 rounded-full bg-[#D6CCB8] dark:bg-stone-700" />
                </div>
                <div className="flex flex-shrink-0 items-center gap-3 border-b border-[#E7DCCB] px-4 pb-3.5 pt-2.5 dark:border-stone-800">
                  {icon ? (
                    <span
                      aria-hidden="true"
                      className={cn('flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-[14px] [&>svg]:h-[21px] [&>svg]:w-[21px]', iconClassName)}
                    >
                      {icon}
                    </span>
                  ) : null}
                  <span className="flex min-w-0 flex-grow flex-col gap-px">
                    <h2 id={titleId} className={cn('m-0 break-words font-display text-[20px] font-bold leading-[1.15]', INK_TEXT)}>{title}</h2>
                    {subtitle ? <span className={cn('text-[13px] leading-snug', MUTED_TEXT)}>{subtitle}</span> : null}
                  </span>
                  {trailing}
                  <CreamClose onClick={onClose} label={closeLabel} />
                </div>
              </>
            )}
            <div className="flex-1 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' } as React.CSSProperties}>
              {children}
            </div>
            {footer ? (
              <div className="flex-shrink-0 border-t border-[#E7DCCB] bg-nva-card/95 px-4 pb-4 pt-3 shadow-[0_-8px_24px_rgba(42,38,32,.06)] dark:border-stone-800 dark:bg-stone-900/95 dark:shadow-none">
                {footer}
              </div>
            ) : null}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default StaySheet;
